/**
 * The payout status vocabulary, in one place.
 *
 * WHY THIS FILE EXISTS
 * Two payment pipelines wrote two different casings for years: the action path
 * and every seed wrote 'pending'/'paid', while this screen's own API handler
 * wrote 'PENDING'/'PAID'. Migration 202610261100 settled it — a CHECK
 * constraint now allows only the lowercase set below, and 'COMPLETED' was
 * normalised to 'paid' on the evidence of a payout_date.
 *
 * Every comparison in the UI used to be exact-case against the uppercase
 * spelling, so the moment the database agreed with itself the money tiles read
 * $0 and the approve button stopped rendering. Comparing raw strings is what
 * caused that, so nothing here compares raw strings: callers normalise first,
 * and the helpers below are the only place a status is interpreted.
 *
 * 'deferred' is new (202610262000). A payment that would breach a benefit
 * ceiling is not refused — it is moved to the next period, and she keeps the
 * money. It must never read as a failure.
 */

import React from 'react';
import { CheckCircle, Clock, XCircle, CalendarClock } from 'lucide-react';

/** The set the database CHECK constraint allows. */
export const PAYOUT_STATUSES = [
  'pending',
  'deferred',
  'approved',
  'processing',
  'paid',
  'failed',
  'cancelled',
  'rejected',
  'void',
] as const;

export type PayoutStatus = (typeof PAYOUT_STATUSES)[number];

/**
 * Lower-case and trim, and fold the one legacy spelling that still turns up in
 * cached responses and old exports. Anything unrecognised is returned as-is so
 * a new status shows up in the UI as itself rather than silently becoming
 * something else.
 */
export function normalizePayoutStatus(status: string | null | undefined): string {
  const s = (status ?? '').trim().toLowerCase();
  if (s === 'completed') return 'paid';
  return s;
}

/** Money in these states is not money she has or will receive. */
export const NOT_RECEIVED: readonly string[] = ['failed', 'cancelled', 'rejected', 'void'];

/** Scheduled or in flight — counts toward a period ceiling, not yet in her hand. */
export const IN_FLIGHT: readonly string[] = ['pending', 'deferred', 'approved', 'processing'];

export const isPaid = (status: string | null | undefined): boolean =>
  normalizePayoutStatus(status) === 'paid';

export const isDeferred = (status: string | null | undefined): boolean =>
  normalizePayoutStatus(status) === 'deferred';

export const isInFlight = (status: string | null | undefined): boolean =>
  IN_FLIGHT.includes(normalizePayoutStatus(status));

export const isFailed = (status: string | null | undefined): boolean =>
  normalizePayoutStatus(status) === 'failed';

export const isAwaitingApproval = (status: string | null | undefined): boolean =>
  normalizePayoutStatus(status) === 'pending';

/** Sum of the payouts whose status passes `predicate`. */
export function sumWhere<T extends { status: string; payout_amount: number }>(
  payouts: T[],
  predicate: (status: string) => boolean,
): number {
  return payouts.filter((p) => predicate(p.status)).reduce((sum, p) => sum + (p.payout_amount || 0), 0);
}

export function countWhere<T extends { status: string }>(
  payouts: T[],
  predicate: (status: string) => boolean,
): number {
  return payouts.filter((p) => predicate(p.status)).length;
}

const LABELS: Record<string, string> = {
  pending: 'Pending',
  deferred: 'Scheduled',
  approved: 'Approved',
  processing: 'Processing',
  paid: 'Paid',
  failed: 'Failed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  void: 'Void',
};

/**
 * 'deferred' reads as "Scheduled" on purpose. To the person waiting for the
 * money, "deferred" sounds like something went wrong; nothing did, and the
 * payment has a date.
 */
export function payoutStatusLabel(status: string | null | undefined): string {
  const s = normalizePayoutStatus(status);
  return LABELS[s] ?? (status ?? '');
}

export function payoutStatusColor(status: string | null | undefined): string {
  switch (normalizePayoutStatus(status)) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'deferred':
      return 'bg-sky-100 text-sky-800 border-sky-200';
    case 'approved':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'processing':
      return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'paid':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'rejected':
    case 'cancelled':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'void':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

export function PayoutStatusIcon({
  status,
  className = 'w-4 h-4',
}: {
  status: string | null | undefined;
  className?: string;
}): React.ReactElement {
  switch (normalizePayoutStatus(status)) {
    case 'paid':
      return React.createElement(CheckCircle, { className: `${className} text-green-600` });
    case 'deferred':
      return React.createElement(CalendarClock, { className: `${className} text-sky-600` });
    case 'pending':
    case 'approved':
    case 'processing':
      return React.createElement(Clock, { className: `${className} text-blue-600` });
    case 'failed':
    case 'rejected':
    case 'cancelled':
    case 'void':
      return React.createElement(XCircle, { className: `${className} text-red-600` });
    default:
      return React.createElement(Clock, { className: `${className} text-gray-600` });
  }
}

/**
 * Options for a status filter. The values are what the API stores, so the
 * filter has to send lower case — the backend compares with lower() on both
 * sides, but sending the stored spelling keeps the URL honest.
 */
export const PAYOUT_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: PayoutStatus; label: string }> =
  PAYOUT_STATUSES.map((value) => ({ value, label: LABELS[value] }));
